import { useMemo, useState, type MouseEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AnimatePresence, motion } from "motion/react";
import { AlertTriangle, Film, Image as ImageIcon, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import {
	api,
	type MetaAdsAdAccount,
	type MetaAdsImportResult,
	type MetaAdsLiveMediaItem,
} from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select } from "@/components/ui/select";

/** The backend caps one import request at this many files. */
const MAX_PICK = 50;

export function ImportFromMetaModal({
	open,
	onClose,
	teamId,
}: {
	open: boolean;
	onClose: () => void;
	teamId?: string | null;
}) {
	const queryClient = useQueryClient();

	const [account, setAccount] = useState<string | null>(null);
	const [picked, setPicked] = useState<Set<string>>(new Set());
	const [results, setResults] = useState<Map<string, MetaAdsImportResult> | null>(null);

	const accountsQuery = useQuery({
		queryKey: ["meta-ads-ad-accounts", teamId ?? null],
		queryFn: () => api.getMetaAdsAdAccounts(teamId),
		enabled: open,
	});

	const usable = useMemo(
		() => (accountsQuery.data?.adAccounts ?? []).filter((a: MetaAdsAdAccount) => a.hasToken),
		[accountsQuery.data],
	);

	const selected =
		account ?? usable.find((a) => a.isDefault)?.dbId ?? usable[0]?.dbId ?? null;

	const liveQuery = useQuery({
		queryKey: ["meta-live-media", selected, teamId ?? null],
		queryFn: () => api.getMetaAdsLiveMedia(selected ?? undefined, teamId),
		enabled: open && Boolean(selected),
	});

	const importMutation = useMutation({
		mutationFn: () =>
			api.importMetaAdsMedia({
				mediaIds: [...picked],
				adAccount: selected ?? undefined,
				teamId,
			}),
		onSuccess: (data) => {
			setResults(new Map(data.results.map((r) => [r.mediaId, r])));
			setPicked(new Set());
			toast.success(
				`Imported ${data.imported}` +
					(data.skipped ? ` · skipped ${data.skipped}` : "") +
					(data.failed ? ` · failed ${data.failed}` : ""),
			);
			queryClient.invalidateQueries({ queryKey: ["files"] });
			queryClient.invalidateQueries({ queryKey: ["meta-live-media"] });
		},
		onError: (err) => {
			toast.error(err instanceof Error ? err.message : "Import failed");
		},
	});

	function toggle(mediaId: string) {
		setPicked((prev) => {
			const next = new Set(prev);
			if (next.has(mediaId)) next.delete(mediaId);
			else if (next.size < MAX_PICK) next.add(mediaId);
			return next;
		});
	}

	function close() {
		if (importMutation.isPending) return;
		setPicked(new Set());
		setResults(null);
		setAccount(null);
		onClose();
	}

	function switchAccount(dbId: string) {
		setAccount(dbId);
		setPicked(new Set());
		setResults(null);
	}

	const media = liveQuery.data?.media ?? [];
	const complete = liveQuery.data?.complete;
	const partial = complete && (!complete.image || !complete.video);

	function rowState(item: MetaAdsLiveMediaItem) {
		const result = results?.get(item.mediaId);
		if (result) {
			if (result.alreadyImported) return { label: "already in library", tone: "neutral" as const };
			if (result.ok) return { label: "✓ imported", tone: "success" as const };
			return { label: `✗ ${result.error ?? "failed"}`, tone: "danger" as const };
		}
		if (item.imported) return { label: "already in library", tone: "neutral" as const };
		return null;
	}

	return (
		<AnimatePresence>
			{open && (
				<motion.div
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					exit={{ opacity: 0 }}
					className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-scrim/40 backdrop-blur-sm"
					onClick={close}
				>
					<motion.div
						initial={{ scale: 0.95, opacity: 0, y: 20 }}
						animate={{ scale: 1, opacity: 1, y: 0 }}
						exit={{ scale: 0.95, opacity: 0, y: 20 }}
						transition={{ type: "spring", damping: 25, stiffness: 300 }}
						className="relative w-full max-w-lg bg-bg-surface border border-border-subtle rounded-2xl shadow-popover flex flex-col max-h-[85vh]"
						onClick={(e: MouseEvent) => e.stopPropagation()}
					>
						<div className="flex items-center justify-between px-6 py-4 border-b border-border-subtle">
							<h3 className="text-text-primary font-semibold">Import from Meta</h3>
							<Button
								variant="ghost"
								size="icon-sm"
								onClick={close}
								disabled={importMutation.isPending}
								className="text-text-tertiary"
							>
								<X size={18} />
							</Button>
						</div>

						<div className="px-6 py-5 overflow-y-auto space-y-4">
							{accountsQuery.isLoading && (
								<div className="flex items-center gap-2 text-text-tertiary text-sm">
									<Loader2 size={14} className="animate-spin" />
									Loading ad accounts…
								</div>
							)}

							{!accountsQuery.isLoading && !usable.length && (
								<p className="text-sm text-text-secondary">
									No Meta ad account is connected. Connect one in settings first.
								</p>
							)}

							{usable.length > 1 && (
								<label className="block">
									<span className="text-xs text-text-tertiary">Ad account</span>
									<Select
										value={selected ?? ""}
										onChange={(e) => switchAccount(e.target.value)}
										disabled={importMutation.isPending}
										className="mt-1"
									>
										{usable.map((a) => (
											<option key={a.dbId} value={a.dbId} className="bg-bg-surface">
												{a.name} · act_{a.adAccountId}
											</option>
										))}
									</Select>
								</label>
							)}

							{liveQuery.isLoading && (
								<div className="flex items-center gap-2 text-text-tertiary text-sm">
									<Loader2 size={14} className="animate-spin" />
									Reading the Meta library…
								</div>
							)}

							{liveQuery.isError && (
								<p className="text-sm text-danger">
									{liveQuery.error instanceof Error
										? liveQuery.error.message
										: "Could not reach Meta."}
								</p>
							)}

							{partial && (
								<div className="flex items-start gap-2 text-xs text-warning bg-warning-bg border border-warning/20 rounded-lg px-3 py-2">
									<AlertTriangle size={14} className="shrink-0 mt-0.5" />
									<span>
										Meta did not return its {!complete.image ? "image" : "video"} list
										— those files are temporarily missing here, not deleted.
									</span>
								</div>
							)}

							{liveQuery.isSuccess && !media.length && (
								<p className="text-sm text-text-tertiary">
									The Meta library of this account is empty.
								</p>
							)}

							{media.length > 0 && (
								<ul className="space-y-1">
									{media.map((item) => {
										const state = rowState(item);
										const disabled =
											Boolean(state) || importMutation.isPending;

										return (
											<li key={item.mediaId}>
												<label
													className={`flex items-start gap-3 text-sm px-3 py-2 rounded-lg ${
														disabled
															? "bg-bg-canvas/50 cursor-default"
															: "bg-bg-canvas cursor-pointer hover:bg-bg-sunken"
													}`}
												>
													<Checkbox
														checked={picked.has(item.mediaId)}
														onChange={() => toggle(item.mediaId)}
														disabled={disabled}
														className="mt-0.5"
													/>
													{item.mediaType === "video" ? (
														<Film size={15} className="text-text-tertiary shrink-0 mt-0.5" />
													) : (
														<ImageIcon size={15} className="text-text-tertiary shrink-0 mt-0.5" />
													)}
													<span
														className={`flex-1 min-w-0 break-words [overflow-wrap:anywhere] ${
															state ? "text-text-tertiary" : "text-text-primary"
														}`}
													>
														{item.name}
													</span>
													{state ? (
														<Badge tone={state.tone} className="shrink-0 mt-0.5">
															{state.label}
														</Badge>
													) : (
														<span className="text-xs shrink-0 mt-0.5 text-text-tertiary">
															{item.mediaType.toUpperCase()}
														</span>
													)}
												</label>
											</li>
										);
									})}
								</ul>
							)}
						</div>

						<div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-border-subtle">
							<span className="text-xs text-text-tertiary">
								{picked.size
									? `Picked ${picked.size}${picked.size >= MAX_PICK ? ` (max ${MAX_PICK})` : ""}`
									: results
										? "Done"
										: ""}
							</span>
							<div className="flex items-center gap-2">
								<Button
									variant="ghost"
									onClick={close}
									disabled={importMutation.isPending}
								>
									{results ? "Done" : "Cancel"}
								</Button>
								<Button
									onClick={() => importMutation.mutate()}
									disabled={!picked.size || importMutation.isPending}
								>
									{importMutation.isPending && (
										<Loader2 size={14} className="animate-spin" />
									)}
									Import{picked.size ? ` ${picked.size}` : ""}
								</Button>
							</div>
						</div>
					</motion.div>
				</motion.div>
			)}
		</AnimatePresence>
	);
}
